import "../../../../App.scss";
import Helper from "../../../../Common/Helper";
import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { IIdentityProviderData } from "./DataModels";
import { PagedResponse } from "@iconics/core";
import { JSX } from "react";
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, LayoutContainer, LayoutContent, RightButtons, TickSymbol } from "@iconics/components";
import { ISecurityProvider, SecurityProviderId } from "../../../../Providers.StaticData/StaticDataProvider";

class ListIdentityProviders extends IbssPage<IRouterProps, IState>
{
    private get labels() { return appContext().labels; }
    private get localStorage() { return appContext().localStorageProvider; }
    private canCreate = false;
    private canUpdate = false;

    constructor(props: IRouterProps)
    {
        super(props);
        this.state =
        {
            identityProvidersGrid: { rows: [] },
            securityProviders: appContext().staticDataProvider.getSecurityProviders(),
        }
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_IdentityProvider_PageTitle;
        this.canCreate = this.localStorage.hasRight("DATAMODEL.IdentityProviders.Create");
        this.canUpdate = this.localStorage.hasRight("DATAMODEL.IdentityProviders.Update");
    }

    private securityProviderName(id: SecurityProviderId): string
    {
        return this.state.securityProviders.find(i => i.id == id)?.name ?? '';
    }

    private async handleRowsRequest(request: IGridRowsRequest<IIdentityProvider>): Promise<IGridRowsResponse<IIdentityProvider>>
    {
        const response = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.identityProviders.get<PagedResponse<IIdentityProviderData[]>>({
            select: 'Id,Name,SecurityProviderTypeId,IsDefault',
            tenancy: appContext().tenantId,
            skipToken: request.skipToken,
            top: request.take,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        const identityProviders = response.value
            .map(i => ({
                id: i.Id,
                name: i.Name,
                securityProviderTypeId: i.SecurityProviderTypeId,
                isActive: true,
                isDefault: i.IsDefault,
            } as IIdentityProvider));

        return { rows: identityProviders, nextSkipToken: response.skipToken };
    }

    public render(): JSX.Element
    {
        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            icon={<SerendieSymbol name='login' />}
                            title={this.labels.Platform_IdentityProvider_ListTitle}
                            subTitle={this.labels.Platform_IdentityProvider_ListSubTitle}
                        >
                            <GridContainer>
                                <GridItem>
                                    <ButtonBar>
                                        <RightButtons>
                                            <Button
                                                size='small'
                                                styleType='ghost'
                                                leftIcon={<SerendieSymbol name='plus' />}
                                                onClick={() => this.props.navigate(`/security-identity-providers/details/0`)}
                                                disabled={!this.canCreate}
                                            >
                                                {this.labels.Platform_CommonAction_Add}
                                            </Button>
                                        </RightButtons>
                                    </ButtonBar>
                                </GridItem>
                                <GridItem>
                                    <DataGrid
                                        columns={[
                                            {
                                                columnId: Helper.nameOf<IIdentityProviderData>("Name"),
                                                title: this.labels.IdentityProvider_Name,
                                                getCellValue: i => i.name,
                                                columnWidth: 40,
                                                sortable: true,
                                            },
                                            {
                                                columnId: Helper.nameOf<IIdentityProviderData>("SecurityProviderTypeId"),
                                                title: this.labels.IdentityProvider_SecurityProviderTypeId,
                                                getCellValue: i => this.securityProviderName(i.securityProviderTypeId),
                                                columnWidth: 40,
                                                sortable: true,
                                            },
                                            {
                                                columnId: Helper.nameOf<IIdentityProviderData>("IsDefault"),
                                                title: this.labels.IdentityProvider_IsDefault,
                                                getCellValue: i => i.isDefault,
                                                template: i => <TickSymbol value={i.value as boolean} />,
                                                columnWidth: 20,
                                                sortable: true,
                                            },
                                        ]}
                                        data={this.state.identityProvidersGrid}
                                        onChange={i => this.setState({ identityProvidersGrid: i })}
                                        rowId={i => i.id.toString()}
                                        onRowsRequest={i => this.handleRowsRequest(i)}
                                        onCellClicked={i => this.props.navigate(`/security-identity-providers/details/${i.id}`)}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                </LayoutContainer>
            </>
        );
    }

}

interface IState
{
    identityProvidersGrid: IGridData<IIdentityProvider>;
    securityProviders: ISecurityProvider[];
}

interface IIdentityProvider
{
    id: number;
    name: string;
    securityProviderTypeId: SecurityProviderId;
    isDefault: boolean;
}

export default withRouting(ListIdentityProviders);
